import React, { useRef, useState, useEffect } from "react"
import Glide from "@glidejs/glide"
import css from "./Swiper.module.scss"
import classNames from "classnames"

/**
 * NOTE: Type definitons does not seem to be 100% correct: https://github.com/glidejs/glide/issues/323
 *
 *
 */

export const Swiper = ({
  perView,
  generalPeek,
  gap,
  dots,
  setBreakpoints,
  onSlideChange,
  children,
}: {
  /**A number of slides visible on a single viewport */
  perView?: number
  /**The general distance value of the next and previous viewports which have to peek in the current view. Accepts number and pixels as a string. Left and right peeking can be set up separately with a directions object. */
  generalPeek?: string | number | Record<"before" | "after", number>
  /**A size of the gap added between slides. */
  gap?: number
  /**Adds slider dots to the track (not clickable)*/
  dots?: boolean
  /**Collection of options applied at specified media breakpoints. */
  setBreakpoints?: Record<string, Partial<Glide.Options>>
  onSlideChange?: (e) => void
  children: Array<React.ReactNode>
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [activeIndices, setActiveIndices] = useState([0, 1])
  const [focusedIndice, setFocusedIndice] = useState([0])
  const [isFirstRender, setIsFirstRender] = useState(true)

  useEffect(() => {
    let slider: Glide
    if (ref.current && !isFirstRender) {
      slider = new Glide(ref.current, {
        type: "slider",
        startAt: 0,
        focusAt: "center",
        perView: perView ?? 1,
        peek: generalPeek ?? 50,
        gap: gap ?? 0,
        rewind: false,
        bound: true,
        breakpoints: setBreakpoints ?? {},
      })

      slider.on("run", () => {
        // Slider index is always the leftmost item
        setActiveIndices([slider.index, slider.index + 1])
        setFocusedIndice([slider.index])
        if (!!onSlideChange) {
          onSlideChange(slider.index)
        }
      })

      //Defaults focused indice to the first element if the slider index changes, for example if the current focused element is removed.
      if (!focusedIndice.includes(slider.index)) {
        setFocusedIndice([0])
      }

      slider.mount()
    }
    return () => {
      slider && slider.destroy()
    }
  }, [isFirstRender, children.length])

  // If Glide tries to mount on first render, it conflicts with Reacts' rendering and does not mount properly.
  useEffect(() => setIsFirstRender(false), [])

  // Returning null here removes the "flashing" of the Swiper children before Glidejs can properly mount

  if (isFirstRender) return null

  return (
    <div
      style={{
        padding: "0px 30px",
      }}
    >
      <div className={css.glide} ref={ref}>
        <div className={css.track} data-glide-el="track">
          <ul className={css.glide__slides}>
            {children.filter(Boolean).map((child, index) => (
              <li
                className={classNames(
                  css.slide,
                  focusedIndice.includes(index) ? css.focused : css.unfocused
                )}
                key={index}
              >
                {/* Makes the non-focused children swipeable, but not clickable and we don't disable the wrapping <li> */}
                <div
                  className={classNames(
                    !focusedIndice.includes(index) && css.noEvents
                  )}
                >
                  {child}
                </div>
              </li>
            ))}
          </ul>
          {dots && (
            <div style={{ marginBottom: "10px" }}>
              {children.map((c, index: number) => (
                <div
                  className={classNames(
                    css.dot,
                    focusedIndice.includes(index) ? css.focused : css.unfocused
                  )}
                  key={index ?? 0}
                ></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
